import React, { useMemo, useState, useRef } from 'react';
import './AudioUploadDialog.scss';
import { ChatApiService } from '../../../utils/chatApiUtils';

interface AudioUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onTranscriptionComplete: (text: string) => void;
  isTranscribing: boolean;
  setIsTranscribing: (transcribing: boolean) => void;
  chatId?: string | null;
  specialty?: string;
  contextHint?: string;
}

const AudioUploadDialog: React.FC<AudioUploadDialogProps> = ({
  isOpen,
  onClose,
  onTranscriptionComplete,
  isTranscribing,
  setIsTranscribing,
  chatId,
  specialty,
  contextHint,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [transcriptionText, setTranscriptionText] = useState('');
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  const [errorText, setErrorText] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = useMemo(() => (
    [
      'audio/aac', 'audio/amr', 'audio/x-aiff', 'audio/aiff', 'audio/flac',
      'audio/mp4', 'audio/x-m4a', 'audio/m4a', 'audio/mpeg', 'audio/mp3',
      'audio/ogg', 'audio/opus', 'audio/wav', 'audio/x-wav', 'audio/webm',
      'audio/wma', 'video/x-ms-wmv' // wmv sometimes detected as video
    ]
  ), []);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!allowedTypes.includes(file.type)) {
        setErrorText('Неподдерживаемый формат. Попробуйте mp3, m4a, wav, ogg и т.д.');
        return;
      }
      setSelectedFile(file);
      setErrorText(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!allowedTypes.includes(file.type)) {
        setErrorText('Неподдерживаемый формат. Попробуйте mp3, m4a, wav, ogg и т.д.');
        return;
      }
      setSelectedFile(file);
      setErrorText(null);
    }
  };

  const handleTranscribe = async () => {
    if (!selectedFile || !chatId) return;
    setErrorText(null);
    setIsTranscribing(true);
    setUploadPercent(0);
    try {
      const serverChatId = parseInt(chatId);
      const resp = await ChatApiService.uploadUserAudio({
        chatId: serverChatId,
        file: selectedFile,
        specialty: specialty || 'общая',
        context: contextHint || undefined,
      }, (p) => setUploadPercent(p));

      if (resp.ok) {
        // Добавляем локальный placeholder, пока ждем итоговое сообщение по SSE
        onTranscriptionComplete('Ожидаем распознавание...');
        try {
          const data = await resp.json();
          if (data && data.text) {
            setTranscriptionText(data.text);
          } else {
            setTranscriptionText('Ожидаем распознавание...');
          }
        } catch {
          setTranscriptionText('Ожидаем распознавание...');
        }
      } else if (resp.status === 401 || resp.status === 403) {
        setErrorText('Авторизация истекла. Войдите снова.');
      } else if (resp.status === 422) {
        setErrorText('Неверный файл или параметры. Попробуйте другой файл.');
      } else {
        setErrorText('Не удалось распознать аудио. Попробуйте другой файл.');
      }
    } catch (error) {
      console.error('Ошибка загрузки аудио:', error);
      setErrorText('Не удалось распознать аудио. Попробуйте другой файл.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleSendToChat = () => {
    if (transcriptionText.trim()) {
      onTranscriptionComplete(transcriptionText);
      onClose();
      setSelectedFile(null);
      setTranscriptionText('');
      setUploadPercent(0);
      setErrorText(null);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedFile(null);
    setTranscriptionText('');
    setUploadPercent(0);
    setErrorText(null);
  };

  if (!isOpen) return null;

  return (
    <div className="audio-upload-overlay" onClick={handleClose}>
      <div className="audio-upload-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="audio-dialog-header">
          <h3>Загрузка аудио файла</h3>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>
        
        <div className="dialog-content">
          {!selectedFile ? (
            <div 
              className={`upload-area ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="upload-icon">🎵</div>
              <p>Перетащите аудио файл сюда или нажмите для выбора</p>
              <p className="file-types">Поддерживаемые форматы: aac, amr, aiff, flac, m4a, mp3, ogg, opus, wav, webm, wma, wmv</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,video/x-ms-wmv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div className="file-selected">
              <div className="file-info">
                <div className="file-icon">🎵</div>
                <div className="file-details">
                  <h4>{selectedFile.name}</h4>
                  <p>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  {selectedFile.size > 100 * 1024 * 1024 && (
                    <p style={{ color: '#b36b00' }}>Файл очень большой (&gt;100MB). Загрузка может занять время.</p>
                  )}
                </div>
              </div>
              {errorText && (
                <div style={{ color: '#c20000', marginTop: '8px' }}>{errorText}</div>
              )}
              
              {!transcriptionText && (
                <button 
                  className="transcribe-button"
                  onClick={handleTranscribe}
                  disabled={isTranscribing}
                >
                  {isTranscribing ? `Загрузка... ${uploadPercent}%` : 'Отправить и распознать'}
                </button>
              )}
              
              {transcriptionText && (
                <div className="transcription-result">
                  <h4>Распознанный текст:</h4>
                  <div className="transcription-text">
                    {transcriptionText}
                  </div>
                  <div className="action-buttons">
                    <button 
                      className="send-button"
                      onClick={handleSendToChat}
                    >
                      Отправить в чат
                    </button>
                    <button 
                      className="retry-button"
                      onClick={() => setTranscriptionText('')}
                    >
                      Распознать заново
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioUploadDialog; 